import React, { PropsWithChildren } from "react";
import { AuthProvider, useAuth } from "react-oidc-context";
import { AuthPreloadRoutes } from "@/features/authentication";
import { WebStorageStateStore } from "oidc-client-ts";
import { useConfig } from "@/hooks/use-config";

/**
 * Auth config injected by the MF host at mount time. When present (embedded),
 * the remote points its OIDC client at the host's authority + client_id so it
 * reads the SAME localStorage session key the host wrote
 * (`oidc.user:{authority}:{client_id}`) and reuses the single host session.
 * When absent (standalone), the remote uses its own env config and logs in
 * independently.
 */
export interface HostAuthConfig {
  authority: string;
  client_id: string;
}

interface AuthProps {
  children: React.ReactNode;
  locale: string;
  /** Host-provided OIDC config; presence means we're embedded. */
  hostAuth?: HostAuthConfig;
}

const isEmbedded =
  typeof window !== "undefined" &&
  (window.parent !== window || !!window.opener);

/** Typed reference to window.__HOST_AUTH__ exposed by TameeniWebsite/mf/auth.js */
interface HostAuthAPI {
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  getUser: () => Promise<unknown>;
  onAuthChange: (cb: (user: unknown) => void) => () => void;
}

function getHostAuth(): HostAuthAPI | undefined {
  return (window as Window & { __HOST_AUTH__?: HostAuthAPI }).__HOST_AUTH__;
}

export default function Auth({
  children,
  locale,
  hostAuth,
}: PropsWithChildren<AuthProps>) {
  const config = useConfig();

  const userStore = React.useMemo(() => {
    if (typeof window !== "undefined") {
      return new WebStorageStateStore({ store: window.localStorage });
    }
    return null;
  }, []);

  // When embedded, override authority+clientId with the host's values so the
  // OIDC client reads the same localStorage key the host already wrote.
  const authority = hostAuth?.authority ?? config.IDENTITY_URL;
  const clientId = hostAuth?.client_id ?? config.IDENTITY_CLIENT_ID;

  const oidcConfig = {
    authority,
    client_id: clientId,
    redirect_uri: config.IDENTITY_REDIRECT_URL,
    client_secret: config.IDENTITY_CLIENT_SECRET,
    scope: "address email openid profile roles offline_access api-profile",
    ui_locales: locale,
    userStore,
    loadUserInfo: true,
    onSigninCallback: function () {
      const isIframe = window.parent !== window;
      const isOidcCallback =
        window.location.search.includes("code=") ||
        window.location.hash.includes("code=");

      if (isIframe && isOidcCallback) {
        // prettier-ignore
        window.parent.postMessage( // NOSONAR
          {
            type: "login-success",
          },
          "*",
        );
      }
    },
  };

  let authContent: React.ReactNode;
  if (hostAuth) {
    // Embedded: sync with host auth state; skip standalone preload routes.
    authContent = (
      <>
        <CrossAppSync
          authority={authority}
          clientId={clientId}
        />
        {children}
      </>
    );
  } else if (isEmbedded) {
    authContent = children;
  } else {
    authContent = <AuthPreloadRoutes>{children}</AuthPreloadRoutes>;
  }

  return <AuthProvider {...oidcConfig}>{authContent}</AuthProvider>;
}

/**
 * Keeps react-oidc-context in sync with the host's auth state.
 *
 * react-oidc-context loads the user once on mount and does not watch
 * localStorage for external writes. Two sources can change auth state:
 *
 * 1. Same-tab: the host fires `mf:authchange` (CustomEvent) after its
 *    UserManager emits userLoaded/userUnloaded.
 * 2. Cross-tab: another tab writes/clears the shared localStorage key, which
 *    triggers the `storage` event here.
 *
 * In both cases we call signinSilent (login) or removeUser (logout) to mirror
 * the change into this provider's React state so useAuth() consumers reflect
 * the current session without a full page reload.
 */
function CrossAppSync({
  authority,
  clientId,
}: {
  authority: string;
  clientId: string;
}) {
  const auth = useAuth();

  // Same-tab: host dispatches mf:authchange after its own UserManager fires.
  React.useEffect(() => {
    const hostAuth = getHostAuth();
    if (!hostAuth) return;

    const unsubscribe = hostAuth.onAuthChange(user => {
      if (user) {
        // Host signed in — pull the token it stored into our OIDC client.
        void auth.signinSilent?.().catch(() => {});
      } else {
        void auth.removeUser();
      }
    });

    return unsubscribe;
  }, [auth]);

  // Cross-tab: storage event fires when another tab writes/clears the key.
  React.useEffect(() => {
    const storageKey = `oidc.user:${authority}:${clientId}`;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== storageKey) return;
      if (e.newValue === e.oldValue) return;
      if (!e.newValue) {
        void auth.removeUser();
      } else {
        void auth.signinSilent?.().catch(() => {});
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [auth, authority, clientId]);

  return null;
}
